import { gql } from '@apollo/client'

import * as f from '../../../components/BPMTable/BPMInputs'
import {
  DataMapper,
  DataTransformer,
  NullClickStrategy,
  StatusComponentCreator,
  BooleanExcelDisplayCreator,
  NumberComponentCreator,
  OnlyFilterActionDisplayStrategy,
  TableFieldDescription,
  GeneralExcelDisplayCreator,
  GeneralChipComponentCreator,
} from '../../../components/BPMTable/TableMapperCreator'
import { ExportFooterItemCreator } from "../../../components/BPMTable/TableFooterItems"
import TableCreatorCreator from '../../../components/BPMTable/TableCreatorFactory/TableCreatorCreator'
import tableCreatorCreatorFactoryCreator from '../../../components/BPMTable/TableCreatorFactory/TableCreatorCreatorFactoryCreator'
import {RawTableQueryBuilder} from "../../../components/BPMTable/TableQueryBuilder/RawTableQueryBuilder";

const tableQuery = gql`
  query InventoryByPeriodReport(
    $orderField:String,
    $first:Int,
    $last:Int,
    $after:String,
    $before:String,
    $period:String!, 
    $currency:String!,
    $serviceNumber:String,
    $serviceType:String,
    $status:Boolean,
    $startDate:String,
    $endDate:String,
    $deviceSerial:String,
    $model:String,
    $brand:String,
    $siteId:String,
    $siteName:String,
    $userEnterpriseId:String,
    $userName:String,
    $invoices:String,
    $accounts:String,
    $vendors:String,
    $costCenters:String,
    ){
    inventoryByPeriodReport(
      orderField:$orderField,
      first:$first,
      last:$last,
      after:$after,
      before:$before,
      period:$period, 
      serviceNumber:$serviceNumber
      serviceType:$serviceType
      status:$status
      startDate:$startDate
      endDate:$endDate
      deviceSerial:$deviceSerial
      model:$model
      brand:$brand
      siteId:$siteId
      siteName:$siteName
      userEnterpriseId:$userEnterpriseId
      userName:$userName
      invoices:$invoices
      accounts:$accounts
      vendors:$vendors
      costCenters:$costCenters
      ) {
      edges{
        node {
          id
          user{
            firstName
            lastName
            enterpriseId
          }
          inventory{
            serviceNumber
            status
            startDate
            endDate
            serviceType{
              name
            }
          }
          invoices {
            invoice {
              identifier
              account {
                identifier
              }
              billingRequest {
                driver {
                  vendor {
                    name
                  }
                }
              }
            }
          }
          device {
            id
            serialNumber
            model {
              id
              name
              brand{
                name
              }
            }
          }
          site {
            enterpriseId
            name
          }
          costCenters {
            costCenter {
              enterpriseId
              name
            }
          }
          amount(currency:$currency)
        }
      }
      count
      pageInfo {
          hasNextPage
          startCursor
          endCursor
      }
    }
  }`

const queries = { tableQueryBuilder: new RawTableQueryBuilder(tableQuery) };

class IventoryReportTableCreator extends TableCreatorCreator {
  constructor(filters) {
    super(filters)
  }

  static getId = () => {
    return "IventoryReportTableCreator"
  }

  getTableCreator = (tableName) => {
    const tableCreator = this.createTableCreator(this.constructor.getId(), tableName, queries, new NullClickStrategy(), new OnlyFilterActionDisplayStrategy())
    tableCreator.setTableFooterItems([new ExportFooterItemCreator()]);

    tableCreator.setDescriptions([
      new TableFieldDescription(
        'serviceNumber',
        new DataMapper("inventory.serviceNumber"),
        new f.FilterDescription(new f.TextFilter("serviceNumber")),
        { label: 'Service Number' }
      ),
      new TableFieldDescription(
        'serviceType',
        new DataMapper("inventory.serviceType.name"),
        new f.FilterDescription(new f.TextFilter("serviceType")),
        { label: 'Service Type' }
      ),
      new TableFieldDescription(
        'status',
        new DataMapper("inventory.status"),
        new f.FilterDescription(new f.BooleanFilter('status')),
        {label: 'Service Status', componentCreator: new StatusComponentCreator(), excelDisplay: new BooleanExcelDisplayCreator()}
      ),
      new TableFieldDescription(
        'startDate',
        new DataMapper("inventory.startDate"),
        new f.FilterDescription(new f.DateFilter("startDate")),
        { label: 'Start Date', onlyForm: true }
      ),
      new TableFieldDescription(
        'endDate',
        new DataMapper("inventory.endDate"),
        new f.FilterDescription(new f.DateFilter("endDate")),
        { label: 'End Date', onlyForm: true }
      ),
      new TableFieldDescription(
        'deviceSerial',
        new DataMapper("device.serialNumber"),
        new f.FilterDescription(new f.TextFilter("deviceSerial")),
        { label: 'Serial Number' }
      ),
      new TableFieldDescription(
        'model',
        new DataMapper("device.model.name"),
        new f.FilterDescription(new f.TextFilter("model")),
        { label: 'Model', onlyForm: true }
      ),
      new TableFieldDescription(
        'brand',
        new DataMapper("device.model.brand.name"),
        new f.FilterDescription(new f.TextFilter("brand")),
        { label: 'Brand', onlyForm: true }
      ),
      new TableFieldDescription(
        'siteId',
        new DataMapper("site.enterpriseId"),
        new f.FilterDescription(new f.TextFilter("siteId")),
        { label: 'Site ID' }
      ),
      new TableFieldDescription(
        'siteName',
        new DataMapper("site.name"),
        new f.FilterDescription(new f.TextFilter("siteName")),
        { label: 'Site Name' }
      ),
      new TableFieldDescription(
        'userEnterpriseId',
        new DataMapper("user.enterpriseId"),
        new f.FilterDescription(new f.TextFilter("userEnterpriseId")),
        { label: 'User ID' }
      ),
      new TableFieldDescription(
        'firstName',
        new DataMapper("user"),
        new f.FilterDescription(new f.TextFilter("userName")),
        { label: 'User Name', transform: new DataTransformer(({ user }) => user ? user.firstName + " " + user.lastName : null)}
      ),
      new TableFieldDescription(
        'invoices',
        new DataMapper("invoices"),
        new f.FilterDescription(new f.TextFilter("invoices")),
        {
          label: 'Invoices',
          hideSort: true,
          excelDisplay: new GeneralExcelDisplayCreator('Invoice', [{ label: 'Identifier', path: 'invoice.identifier' }], 0),
          componentCreator: new GeneralChipComponentCreator(["invoice.identifier"], ["invoice.identifier"], null, false),
          transform: new DataTransformer(({ invoices }) => invoices),
        }
      ),
      new TableFieldDescription(
        'accounts',
        new DataMapper("invoices"),
        new f.FilterDescription(new f.TextFilter("accounts")),
        {
          label: 'Accounts',
          hideSort: true,
          excelDisplay: new GeneralExcelDisplayCreator('Account', [{ label: 'Identifier', path: 'identifier' }], 0),
          componentCreator: new GeneralChipComponentCreator(["identifier"], ["identifier"], null, false),
          transform: new DataTransformer(({ invoices }) => {
            const accounts = invoices.map(e => e.invoice.account);
            const uniqueAccounts = new Map(accounts.map(obj => [obj.identifier, obj])).values();
            return  Array.from(uniqueAccounts);
          }),
        }
      ),
      new TableFieldDescription(
        'vendors',
        new DataMapper("invoices"),
        new f.FilterDescription(new f.TextFilter("vendors")),
        {
          label: 'Vendors',
          hideSort: true,
          excelDisplay: new GeneralExcelDisplayCreator('Vendor', [{ label: 'name', path: 'name' }], 0),
          componentCreator: new GeneralChipComponentCreator(["name"], ["name"], null, false),
          transform: new DataTransformer(({ invoices }) => {
            const vendors = invoices.map(e => e.invoice.billingRequest.driver.vendor);
            const uniqueVendors = new Map(vendors.map(obj => [obj.identifier, obj])).values();
            return  Array.from(uniqueVendors);
          }),
        }
      ),
      new TableFieldDescription(
        'costCenters',
        new DataMapper("costCenters"),
        new f.FilterDescription(new f.TextFilter("costCenters")),
        {
          label: "Cost Center",
          hideSort: true,
          draggable: false,
          excelDisplay: new GeneralExcelDisplayCreator('Cost Center', [{ label: 'Name', path: 'costCenter.displayName' }]),
          componentCreator: new GeneralChipComponentCreator(['costCenter.displayName'], null, 'Not Assigned'),
          transform: new DataTransformer(({ costCenters }) => costCenters ? costCenters : []),
        }
      ),
      new TableFieldDescription(
        'amount',
        new DataMapper("amount"),
        new f.FilterDescription(new f.NullCreator("amount")),
        { 
          label: 'Amount', 
          componentCreator: new NumberComponentCreator(this.parentProps.symbol, 2), 
          excelTransform: new DataTransformer(({amount})=>Number(amount).toFixed(2)),
          fieldAlign: 'right',
        }
      ),
    ])

    return tableCreator;
  }
}

tableCreatorCreatorFactoryCreator.addTableCreatorCreator(IventoryReportTableCreator)

export default IventoryReportTableCreator;
